import { useState, useCallback } from 'react';
import { personService } from '@shared/services/personService';
import useModal from '@shared/hooks/useModal';
import useFetchData from '@shared/hooks/useFetchData';
import { useEditHandler } from '@shared/hooks/useEditHandler';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { useTranslation } from 'react-i18next';

export const usePerson = () => {
    const { t } = useTranslation();
    const [personData, setPersonData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedActive, setSelectedActive] = useState('true');

    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const {
        data,
        error,
        pageNumber,
        selectedData,
        setSelectedData,
        pageSize,
        searchQuery,
        searchParams,
        totalPages,
        totalElements,
        searchType,
        setSearchQuery,
        setSearchType,
        handleSearchClick,
        fetchData,
        handleSort,
        sortBy,
        sortDir,
        setPageSize,
        setpageNumber,
        setTotalElements,
        setTotalPages
    } = useFetchData();

    const buildFetchParams = useCallback((overrides = {}) => {
        const params = {
            searchQuery,
            sortBy: sortBy || 'name',
            sortDir,
            ...overrides,
        };

        if (selectedActive !== null && selectedActive !== '') {
            params.active = selectedActive === 'true';
        }

        return params;
    }, [searchQuery, sortBy, sortDir, selectedActive]);

    const fetchPersons = useCallback((getData, params = {}) => {
        return fetchData(getData, {
            ...buildFetchParams(),
            ...params,
        });
    }, [fetchData, buildFetchParams]);

    const handlePersonSearchClick = useCallback((getData, params = {}) => {
        return handleSearchClick(getData, {
            ...buildFetchParams(),
            ...params,
        });
    }, [handleSearchClick, buildFetchParams]);

    const handleEditPerson = useEditHandler({
        data,
        selectedData,
        setSelectedItem: setPersonData,
        handleOpenModal,
        errorMessage: 'No person selected for editing'
    });

    const handleConfirmDelete = useDeleteHandler({
        service: personService,
        entityName: 'Person',
        selectedData,
        fetchData: fetchPersons,
        setSelectedData,
        handleCloseModal,
    });

    const handleDeactivatePerson = useCallback(async () => {
        try {
            if (!selectedData) {
                notifyError(t('common.noSelection'));
                return;
            }

            await personService.deactivatePerson(selectedData);
            notifySuccess(t('persons.deactivateSuccess'));
            setSelectedData(null);
            await fetchPersons(personService.getPersons);
        } catch (error) {
            notifyError(t('persons.deactivateError'));
            console.error('Error deactivating person:', error);
        }
    }, [selectedData, fetchPersons, t]);

    const handleActivatePerson = useCallback(async () => {
        try {
            if (!selectedData) {
                notifyError(t('common.noSelection'));
                return;
            }

            await personService.activatePerson(selectedData);
            notifySuccess(t('persons.activateSuccess'));
            setSelectedData(null);
            await fetchPersons(personService.getPersons);
        } catch (error) {
            notifyError(t('persons.activateError'));
            console.error('Error activating person:', error);
        }
    }, [selectedData, fetchPersons, t]);

    const handleAddCloseModal = useCallback(() => {
        handleCloseModal();
        setPersonData(null);
    }, [handleCloseModal]);

    const searchOptions = [
        { key: 'name', label: 'firstName' },
        { key: 'fathersName', label: 'fathersName' },
        { key: 'surname', label: 'surname' },
        { key: 'nationality', label: 'nationality' },
        { key: 'idn', label: 'idn' },
    ];

    const selectedPersonName = selectedData !== null
        ? data?.find(person => person.id === selectedData)?.name
        : '';

    return {
        data,
        error,
        loading,
        personData,
        selectedData,
        modalState,

        pageNumber,
        pageSize,
        sortBy,
        sortDir,
        totalElements,
        totalPages,

        searchQuery,
        searchType,
        searchOptions,
        searchParams,

        handleOpenModal,
        handleCloseModal,
        handleAddCloseModal,
        handleEditPerson,
        handleConfirmDelete,
        handleDeactivatePerson,
        handleActivatePerson,
        handlePersonSearchClick,
        handleSearchClick,
        setSearchQuery,
        setSearchType,
        handleSort,
        setPageSize,
        setpageNumber,
        setTotalElements,
        setTotalPages,
        setSelectedData,
        setPersonData,
        selectedPersonName,
        selectedActive,
        setSelectedActive,
        buildFetchParams,
        fetchPersons,
        fetchData,
        personService
    };
}; 